import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VerdictPropagationDashboard = ({ propagationData, onRetryFailed, onViewDetails }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');

  const timeframes = [
    { value: '1h', label: '1 Hour' },
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'text-success';
      case 'pending': return 'text-warning';
      case 'failed': return 'text-error';
      case 'processing': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'failed': return 'XCircle';
      case 'processing': return 'Loader';
      default: return 'Circle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Verdict Propagation Dashboard</h2>
          <p className="text-sm text-muted-foreground">Real-time publishing status across platforms</p>
        </div>
        <div className="flex items-center space-x-2">
          {timeframes?.map((timeframe) => (
            <Button
              key={timeframe?.value}
              variant={selectedTimeframe === timeframe?.value ? "default" : "ghost"}
              size="sm"
              onClick={() => setSelectedTimeframe(timeframe?.value)}
            >
              {timeframe?.label}
            </Button>
          ))}
        </div>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-success/10 border border-success/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-success">{propagationData?.summary?.delivered}</div>
          <div className="text-sm text-success/80">Delivered</div>
        </div>
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-warning">{propagationData?.summary?.pending}</div>
          <div className="text-sm text-warning/80">Pending</div>
        </div>
        <div className="bg-error/10 border border-error/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-error">{propagationData?.summary?.failed}</div>
          <div className="text-sm text-error/80">Failed</div>
        </div>
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-primary">{propagationData?.summary?.processing}</div>
          <div className="text-sm text-primary/80">Processing</div>
        </div>
      </div>
      {/* Recent Propagations */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-foreground">Recent Propagations</h3>
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            onClick={() => window.location?.reload()}
          >
            Refresh
          </Button>
        </div>

        <div className="space-y-2">
          {propagationData?.recentPropagations?.map((propagation) => (
            <div key={propagation?.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth">
              <div className="flex items-center space-x-4">
                <div className={`p-2 rounded-full bg-background ${getStatusColor(propagation?.status)}`}>
                  <Icon 
                    name={getStatusIcon(propagation?.status)} 
                    size={16}
                    className={propagation?.status === 'processing' ? 'animate-spin' : ''}
                  />
                </div>
                <div>
                  <div className="font-medium text-foreground">{propagation?.verdictTitle}</div>
                  <div className="text-sm text-muted-foreground">
                    {propagation?.platformCount} platforms • {propagation?.timestamp}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="text-right">
                  <div className="text-sm font-medium text-foreground">
                    {propagation?.successCount}/{propagation?.totalCount}
                  </div>
                  <div className="text-xs text-muted-foreground">Success Rate</div>
                </div>
                <div className="flex space-x-1">
                  {propagation?.status === 'failed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="RotateCcw"
                      onClick={() => onRetryFailed(propagation?.id)}
                    >
                      Retry
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    onClick={() => onViewDetails(propagation?.id)}
                  >
                    Details
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Platform Status Grid */}
      <div className="mt-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Platform Status Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {propagationData?.platformStatus?.map((platform) => (
            <div key={platform?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Globe" size={16} className="text-primary" />
                  </div>
                  <span className="font-medium text-foreground">{platform?.name}</span>
                </div>
                <div className={`w-3 h-3 rounded-full ${platform?.online ? 'bg-success' : 'bg-error'}`} />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Sync:</span>
                  <span className="text-foreground">{platform?.lastSync}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Response Time:</span>
                  <span className="text-foreground">{platform?.responseTime}ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Success Rate:</span>
                  <span className={`font-medium ${platform?.successRate >= 95 ? 'text-success' : platform?.successRate >= 80 ? 'text-warning' : 'text-error'}`}>
                    {platform?.successRate}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VerdictPropagationDashboard;